/**
 * Reads a Cmd+K query as a natural-language prompt: which side, how much, which assets, which
 * execution style, and whether it reads more like a trade or a research question. Tolerates
 * common typos ("by 1 btc", "sel eth", "twpa") so the palette can still offer the prompt, and
 * reports what it corrected. Pure — the palette feeds it the known asset symbols.
 */

export type PromptSide = 'buy' | 'sell'

export type PromptStrategy =
  | 'twap'
  | 'vwap'
  | 'dca'
  | 'iceberg'
  | 'chase'
  | 'grid'
  | 'scale'
  | 'limit'
  | 'market'
  | 'stop'
  | 'tp'
  | 'sl'
  | 'close'

export interface PromptCorrection {
  from: string
  to: string
}

export interface PromptIntent {
  /** Which prompt to offer first: a trade instruction, or a question for the assistant. */
  primary: 'trade' | 'research'
  /**
   * A side, an amount or an execution algo was typed — clearly a prompt, so the palette puts
   * it above its regular commands. Otherwise (just a ticker, an order-type word like "market",
   * or research wording) it's offered below them, since those also match command names.
   */
  strong: boolean
  side?: PromptSide
  /** First amount-looking token, as typed ("10", "0.5", "$500", "25%", "2k"). */
  quantity?: string
  /** Recognised asset symbols, uppercased, in the order typed. */
  assets: string[]
  strategy?: PromptStrategy
  corrections: PromptCorrection[]
  /** The query with whitespace collapsed and side/strategy typos corrected — what gets sent. */
  text: string
}

const SIDE_WORDS: Record<string, PromptSide> = {
  buy: 'buy',
  long: 'buy',
  bid: 'buy',
  sell: 'sell',
  short: 'sell',
  매수: 'buy',
  롱: 'buy',
  매도: 'sell',
  숏: 'sell',
}

const STRATEGY_WORDS: Record<string, PromptStrategy> = {
  twap: 'twap',
  vwap: 'vwap',
  dca: 'dca',
  iceberg: 'iceberg',
  chase: 'chase',
  grid: 'grid',
  scale: 'scale',
  ladder: 'scale',
  limit: 'limit',
  market: 'market',
  stop: 'stop',
  tp: 'tp',
  sl: 'sl',
  close: 'close',
  분할: 'scale',
  지정가: 'limit',
  시장가: 'market',
  청산: 'close',
}

// Misspellings too short/ambiguous for edit distance, mapped explicitly.
const EXPLICIT_TYPOS: Record<string, string> = {
  by: 'buy',
  bye: 'buy',
  bui: 'buy',
  sel: 'sell',
  sll: 'sell',
  shor: 'short',
  lng: 'long',
  lon: 'long',
  mkt: 'market',
  lmt: 'limit',
  lim: 'limit',
}

const RESEARCH_WORDS = new Set([
  'why',
  'what',
  'how',
  'when',
  'should',
  'news',
  'analysis',
  'analyze',
  'analyse',
  'research',
  'explain',
  'compare',
  'outlook',
  'forecast',
  'sentiment',
  'summarize',
  '분석',
  '뉴스',
  '왜',
  '전망',
  '비교',
  '설명',
  '요약',
])

// Always recognised even before instrument lists load.
export const COMMON_PROMPT_ASSETS = [
  'BTC',
  'ETH',
  'SOL',
  'HYPE',
  'XRP',
  'DOGE',
  'ZEC',
  'BNB',
  'ADA',
  'AVAX',
  'LINK',
  'TRX',
  'SUI',
  'LTC',
  'PAXG',
] as const

const QUANTITY_RE = /^[$€]?\d+(?:[.,]\d+)?(?:[kKmM]|%|\$)?$/
// "btcusdt", "btc-perp", "btc/usd" → "btc"
const ASSET_SUFFIX_RE = /[-_/]?(usdt|usdc|usd|perp|perpetual|spot)$/i

/** Optimal string alignment distance (Levenshtein + adjacent transposition). */
function editDistance(a: string, b: string): number {
  const d: number[][] = Array.from({ length: a.length + 1 }, (_, i) =>
    Array.from({ length: b.length + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
  )
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost)
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1)
      }
    }
  }
  return d[a.length][b.length]
}

/** Nearest dictionary word within one edit, for ASCII tokens of 4+ letters. */
function fuzzyWord(token: string, words: string[]): string | null {
  if (token.length < 4 || !/^[a-z]+$/.test(token)) return null
  let best: string | null = null
  for (const word of words) {
    if (word.length < 4 || Math.abs(word.length - token.length) > 1) continue
    if (editDistance(token, word) === 1) {
      if (best) return null // ambiguous
      best = word
    }
  }
  return best
}

// Execution algos — unambiguous enough to lead the palette on their own.
const ALGO_STRATEGIES = new Set<PromptStrategy>(['twap', 'vwap', 'dca', 'iceberg', 'chase'])

const SIDE_LIST = Object.keys(SIDE_WORDS)
const STRATEGY_LIST = Object.keys(STRATEGY_WORDS)

function resolveAsset(token: string, knownAssets: ReadonlySet<string>): string | null {
  const upper = token.toUpperCase()
  if (knownAssets.has(upper)) return upper
  const stripped = token.replace(ASSET_SUFFIX_RE, '').toUpperCase()
  if (stripped && stripped !== upper && knownAssets.has(stripped)) return stripped
  return null
}

/**
 * Returns null when nothing in the query suggests a prompt (no side, strategy, asset or
 * research wording) — the palette then shows only its regular commands.
 */
export function parsePromptIntent(
  query: string,
  knownAssets: ReadonlySet<string>
): PromptIntent | null {
  const rawTokens = query.trim().split(/\s+/).filter(Boolean)
  if (rawTokens.length === 0) return null

  let side: PromptSide | undefined
  let strategy: PromptStrategy | undefined
  let quantity: string | undefined
  let researchHits = 0
  const assets: string[] = []
  const corrections: PromptCorrection[] = []
  const outTokens: string[] = []

  rawTokens.forEach((raw, index) => {
    const token = raw.toLowerCase().replace(/[,.!?]+$/, '')
    let word = token

    // A real asset symbol always wins over a typo guess ("sol" isn't "sell").
    const asset = resolveAsset(token, knownAssets)
    if (asset) {
      if (!assets.includes(asset)) assets.push(asset)
      outTokens.push(raw)
      return
    }

    if (!(word in SIDE_WORDS) && !(word in STRATEGY_WORDS)) {
      // "by" only reads as "buy" leading the prompt ("twap by 1h" keeps its meaning).
      const explicit = EXPLICIT_TYPOS[word]
      const fixed =
        explicit && (explicit !== 'buy' || index === 0)
          ? explicit
          : (fuzzyWord(word, SIDE_LIST) ?? fuzzyWord(word, STRATEGY_LIST))
      if (fixed) {
        corrections.push({ from: raw, to: fixed })
        word = fixed
      }
    }

    if (word in SIDE_WORDS) {
      side ??= SIDE_WORDS[word]
    } else if (word in STRATEGY_WORDS) {
      strategy ??= STRATEGY_WORDS[word]
    } else if (QUANTITY_RE.test(token)) {
      quantity ??= token
    } else if (RESEARCH_WORDS.has(word) || raw.endsWith('?')) {
      researchHits++
    }
    outTokens.push(word === token ? raw : word)
  })

  if (!side && !strategy && assets.length === 0 && researchHits === 0) return null

  const tradeSignal = Boolean(side || strategy)
  return {
    primary: tradeSignal && researchHits === 0 ? 'trade' : researchHits > 0 ? 'research' : 'trade',
    strong: Boolean(side || quantity || (strategy && ALGO_STRATEGIES.has(strategy))),
    side,
    quantity,
    assets,
    strategy,
    corrections,
    text: outTokens.join(' '),
  }
}

/**
 * Example prompts for a query that names a style or side but little else ("twap", "buy"),
 * using the typed asset when there is one. Picking one fills the search.
 */
export function promptExamples(intent: PromptIntent): string[] {
  const asset = intent.assets[0] ?? 'BTC'
  const sideWord = intent.side ?? 'buy'
  const detailCount = (intent.quantity ? 1 : 0) + intent.assets.length
  if (detailCount >= 2) return []

  switch (intent.strategy) {
    case 'twap':
      return [
        `${sideWord} 1 ${asset} twap over 1h`,
        `${sideWord} 5 ${asset} twap over 30m, 10 slices`,
      ]
    case 'vwap':
      return [`${sideWord} 2 ${asset} vwap over 4h`, `${sideWord} 10 ${asset} vwap until close`]
    case 'dca':
      return [`dca $100 into ${asset} every day for 10 days`, `dca $500 into ${asset} weekly`]
    case 'iceberg':
      return [`${sideWord} 10 ${asset} iceberg, show 1 at a time`]
    case 'chase':
      return [`chase ${sideWord} 0.5 ${asset}`, `chase ${sideWord} 1 ${asset} up to 0.2% away`]
    case 'grid':
    case 'scale':
      return [`${sideWord} 3 ${asset} scaled in 5 orders from -1% to -5%`]
    case 'limit':
      return [`${sideWord} 1 ${asset} limit at best bid`]
    case 'market':
      return [`${sideWord} 0.1 ${asset} at market`]
    case 'stop':
    case 'sl':
      return [`stop loss on my ${asset} long at -5%`]
    case 'tp':
      return [`take profit on my ${asset} position at +10%`]
    case 'close':
      return [`close my ${asset} position`, `close half of my ${asset} position`]
  }
  if (intent.side) {
    return [
      `${sideWord} 0.1 ${asset} at market`,
      `${sideWord} 1 ${asset} twap over 1h`,
      `${sideWord} $1000 of ${asset} limit -1%`,
    ]
  }
  return []
}
